import { FC } from "react";

/**
 * Interface representing a single schedule entry.
 * This structure is used to define the operational timings for each day at a specific center.
 * 
 * Properties:
 * - `id`: A unique identifier for the schedule entry.
 * - `centre_id`: The ID of the center associated with the schedule.
 * - `day`: The day of the week (e.g., "Monday").
 * - `start_time`: The opening time of the center on the specified day.
 * - `end_time`: The closing time of the center on the specified day.
 * - `created_at`: The timestamp when the schedule entry was created.
 * - `updated_at`: The timestamp when the schedule entry was last updated.
 * - `closed` (optional): A flag indicating if the center is closed on this day.
 */
interface ScheduleItem {
  id: number;
  centre_id: number;
  day: string;
  start_time: string;
  end_time: string;
  created_at: string;
  updated_at: string;
  closed?: boolean; // Optional property for closed days
}

/**
 * Interface representing the props passed to a component for rendering the schedule.
 * 
 * Properties:
 * - `schedule`: An array of `ScheduleItem` objects, encapsulating the operational timings for all days.
 */
interface CentreTimingProps {
  schedule: ScheduleItem[];
}

/**
 * The `CentreTimings` component displays the schedule for a center.
 * 
 * - Props:
 *   - `schedule`: An array of objects, each representing a day's timings.
 * - Features:
 *   - Shows each day and its corresponding schedule in order (Monday to Sunday).
 *   - Marks a day as "Closed" if no timings are available.
 *   - Formats the timings into a 12-hour format for readability.
 */
const CentreTimings: FC<CentreTimingProps> = ({ schedule }) => {
  const daysOrder: string[] = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  const completeSchedule: ScheduleItem[] = daysOrder.map((day) => {
    const daySchedule = schedule?.find(
      (item: ScheduleItem) => item.day === day
    );
    return daySchedule
      ? daySchedule
      : ({ day, start_time: "", end_time: "", closed: true } as ScheduleItem);
  });

  const convertTo12HourFormat = (time: string) => {
    // Split the time into hours and minutes
    let [hours, minutes] = time.split(":").map(Number);

    // Determine AM or PM
    const period = hours >= 12 ? "PM" : "AM";

    // Convert hours from 24-hour to 12-hour format
    hours = hours % 12 || 12; // Convert '0' or '12' to '12'

    // Format the hours and minutes to always be two digits
    const formattedHours = hours.toString().padStart(2, "0");
    const formattedMinutes = minutes.toString().padStart(2, "0");

    // Return the formatted time
    return `${formattedHours}:${formattedMinutes} ${period}`;
  };

  return (
    <>
      {completeSchedule.map((item: ScheduleItem, index: number) => (
        <div key={index} className="d-flex gap-3 align-items-center">
          <div className="min-w-50px">{item.day.substring(0, 3)}</div>
          {item.closed ? (
            "Closed"
          ) : (
            <div className="text-lowercase">
              {`${convertTo12HourFormat(
                item.start_time
              )} - ${convertTo12HourFormat(item.end_time)}`}
            </div>
          )}
        </div>
      ))}
    </>
  );
};

export default CentreTimings;
